import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, NavigationIcon, MapPinIcon } from 'lucide-react';
import { ROUTES } from '../data/routes';
const VIDEO_URL = 'https://i.imgur.com/Omfv5ra.mp4';
const MAP_EMBED_URL = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15927.5!2d-74.9256!3d4.2876!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8e3f5b0000000000%3A0x0000000000000000!2sPayand%C3%A9%2C+San+Luis%2C+Tolima!5e0!3m2!1ses!2sco!4v1234567890';
export function HowToArriveSection() {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleLoaded = () => {
      video.playbackRate = 0.7;
    };
    video.addEventListener('loadedmetadata', handleLoaded);
    return () => video.removeEventListener('loadedmetadata', handleLoaded);
  }, []);
  return <section id="como-llegar" className="relative bg-night px-6 sm:px-8 lg:px-12 py-20 md:py-28 overflow-hidden" data-id="element-247" data-mp="1t4h5ubhcu5qv">
      <div className="absolute inset-0 z-0" aria-hidden="true" data-id="element-248" data-mp="3pewgm75wnzav">
        <video ref={videoRef} className="w-full h-full object-cover opacity-[0.15]" autoPlay muted loop playsInline data-id="element-249" data-mp="3lema8te7b6vh">
          <source src={VIDEO_URL} type="video/mp4" data-id="element-250" data-mp="39966vl7pdd8" />
        </video>
      </div>
      <div className="absolute inset-0 z-[1]" style={{
      background: 'linear-gradient(to bottom, #0a0a0a 0%, rgba(10,10,10,.6) 50%, #0a0a0a 100%)'
    }} aria-hidden="true" data-id="element-251" data-mp="3uf0urqjcchxx" />

      <div className="relative z-[2] max-w-6xl mx-auto" data-id="element-252" data-mp="31qmybf7whtoh">
        <motion.div initial={{
        opacity: 0,
        y: 35
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-40px'
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-253" data-mp="31rxyz661ulbd">
          <span className="inline-block text-[0.67rem] font-semibold tracking-[0.3em] uppercase text-gold mb-5" data-id="element-254" data-mp="2nyf3ht06gcsf">
            Cómo Llegar
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.15] text-white" data-id="element-255" data-mp="2izlr63wmmrtu">
            Más cerca de lo
            <br data-id="element-256" data-mp="rvk6qe924ruf" />
            <em className="italic text-gold" data-id="element-257" data-mp="rv0looo41nc6">que imaginas</em>
          </h2>
          <p className="text-sm leading-[1.8] text-white/45 max-w-xl mx-auto mt-4" data-id="element-258" data-mp="wmsfr96h6b72">
            Vereda El Retiro, Payandé · San Luis, Tolima
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start" data-id="element-259" data-mp="10pyii6udpk8s">
          <div className="flex flex-col gap-3.5" data-id="element-260" data-mp="3zpmavgnoqno">
            {ROUTES.map((route, i) => <motion.div key={route.city} initial={{
            opacity: 0,
            y: 35
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.8,
            delay: i * 0.1
          }} className="flex items-center gap-5 px-7 py-5 bg-white/[0.04] border border-white/[0.07] rounded-2xl transition-all duration-300 hover:bg-white/[0.07] hover:border-gold/[0.18]" data-id="element-261" data-mp="8x7wep6etbx9" data-mpi={i}>
                <div className="w-[42px] h-[42px] bg-gradient-to-br from-forest-900 to-forest-700 rounded-full flex items-center justify-center shrink-0" data-id="element-262" data-mp="23g83vhkjgu32">
                  <ClockIcon className="w-[19px] h-[19px] text-gold" strokeWidth={1.5} data-id="element-263" data-mp="1tqesagkeceyt" />
                </div>
                <div data-id="element-264" data-mp="1pq4n364fhsl0">
                  <p className="text-sm font-medium text-white" data-id="element-265" data-mp="1pojtpjtuofoq">{route.city}</p>
                  <p className="font-display text-xl text-gold leading-tight" data-id="element-266" data-mp="1kqi7wr51i29k">{route.duration}</p>
                  <p className="text-[0.7rem] text-white/40 mt-0.5" data-id="element-267" data-mp="1fqvv2it4io1a">{route.description}</p>
                </div>
              </motion.div>)}

            <motion.div initial={{
            opacity: 0,
            y: 35
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.8,
            delay: 0.4
          }} className="flex gap-3 flex-wrap mt-3" data-id="element-268" data-mp="1au58dcmtcu6w">
              <a href="https://waze.com/ul?q=Refugio+Caracoli+Glamping+Payande+Tolima" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#33ccff] text-black px-6 py-3 rounded-full text-[0.76rem] font-semibold tracking-wide uppercase transition-all hover:bg-[#66d9ff] hover:-translate-y-0.5" data-id="element-269" data-mp="312nkyi91zpq3">
                <NavigationIcon className="w-[15px] h-[15px]" data-id="element-270" data-mp="2ph70mv5idso1" />
                Abrir en Waze
              </a>
              <a href="https://maps.google.com/?q=Payande+San+Luis+Tolima+Colombia" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white/[0.07] text-white border border-white/[0.18] px-6 py-3 rounded-full text-[0.76rem] font-medium tracking-wide uppercase transition-all hover:bg-white/[0.14] hover:-translate-y-0.5" data-id="element-271" data-mp="2u7neggd8rgax">
                <MapPinIcon className="w-[15px] h-[15px]" data-id="element-272" data-mp="2bg4c8br1k69y" />
                Google Maps
              </a>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 35
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="bg-white/[0.04] border border-white/[0.07] rounded-[40px] overflow-hidden h-[260px] sm:h-[360px]" data-id="element-273" data-mp="1rxgsmyihux8y">
            <iframe src={MAP_EMBED_URL} title="Mapa Refugio Caracolí" loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="w-full h-full border-0" data-id="element-274" data-mp="2at6wysdwg5q0" />
          </motion.div>
        </div>
      </div>
    </section>;
}